import { useMemo, useState } from 'react';
import { useAppDispatch, useAppTranslation, useErrTranslation } from 'app/hooks';
import Typo from 'consts/typography';
import { formatAmountV2 } from 'utils/formatAmount';
import { isValidAmount } from '../Modals/WithdrawModal';
import { StakingVaultResponse } from 'models/stake';
import stakingApi from 'api/stakingApi';
import { useHandleGetWallets } from 'hooks/useHandleGetWallets';
import { showErrorModal, showSuccessModal } from 'components/Modals/CommonModals';
import { setUpdateStakeListResult } from 'redux/userSlice';

interface StakingFormProps {
  onShowLoading: (showLoading: boolean) => void;
  isLoading: boolean;
  vaults: StakingVaultResponse[];
  redirectToHistory: () => void;
}

const StakingFormOffChain: React.FunctionComponent<StakingFormProps> = ({
  onShowLoading,
  isLoading,
  vaults,
  redirectToHistory,
}) => {
  const t = useAppTranslation();
  const et = useErrTranslation();

  const [option, setOption] = useState<StakingVaultResponse>(vaults[0]);
  const [amount, setAmount] = useState<string>('');
  const dispatch = useAppDispatch();

  const { tbetBalance, getWallet } = useHandleGetWallets();

  const handleChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^$|^(1000000(\.0{0,2})?|[0-9]{1,6}(\.[0-9]{0,2})?|0\.)$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setAmount(value);
    }
  };

  const handleChangeVaultClick = (item: StakingVaultResponse) => {
    setOption(item);
  };

  const canClickableButton = useMemo(() => {
    if (!Number(amount)) return true;
    if (Number(amount) > Number(tbetBalance())) return true;
  }, [tbetBalance(), amount]);

  const stake = async () => {
    const { ok, error } = await stakingApi.stake({
      vaultId: option.id,
      amount: parseFloat(amount),
    });
    if (ok) {
      getWallet();
      setAmount('');
      showSuccessModal({
        content: t('staking.success.title'),
      });
      dispatch(setUpdateStakeListResult());
    }
    if (error) {
      showErrorModal({
        content: error.message.includes('staking-min.invalid')
          ? et(error.message, { minAmount: option.minStake })
          : et(error.message),
        onOk: () => {},
        title: et(error.title || '', { defaultValue: et('defaultMessage.title') }),
      });
    }
  };

  const handleGoStakingHistory = () => {
    redirectToHistory();
  };

  return (
    vaults && (
      <div className="staking-form-offchain">
        <div className="staking-form__header">
          <h3 className={`${Typo.p2} primary-text-color text-center font-semibold`}>
            {t('staking.form.stakingPool')}
          </h3>
          <div className="staking-form__info">
            <h4 className={`${Typo.p4} primary-text-color font-semibold uppercase`}>
              {t('staking.form.stakingRate')}
            </h4>
            <h2 className="stacking-percent">
              {option?.rewardPercent}% <span>APY</span>
            </h2>
            <div className=" rounded-full stacking-bar ">
              {vaults &&
                vaults.map((item) => (
                  <span
                    key={item.id}
                    className={`rounded-full stacking-option  ${
                      item.id === option?.id ? 'active' : ''
                    }`}
                  ></span>
                ))}
            </div>
            <h4 className="upper-case mt-20 primary-text-color font-semibold uppercase">
              {option?.label}
            </h4>
          </div>
        </div>
        <div className="staking-form__form">
          {isLoading ? (
            <span className="spiner-loader"></span>
          ) : (
            <>
              <form>
                <label className="primary-text-color">{t('staking.form.amount')}</label>
                <input
                  className="amount-input"
                  type="text"
                  value={amount}
                  onChange={handleChangeAmount}
                />
                <div className="stake-allowance">
                  <span> {t('staking.form.allowance')} </span>
                  <span>$TBET {formatAmountV2(tbetBalance(), 8)}</span>
                </div>
                <div className="percen-group">
                  {vaults &&
                    vaults.map((vault) => (
                      <button
                        key={vault.id}
                        type="button"
                        className={`uppercase percen-button ${
                          option?.id === vault.id ? 'active' : ''
                        }`}
                        onClick={() => handleChangeVaultClick(vault)}
                      >
                        {vault.label}
                      </button>
                    ))}
                </div>
              </form>
              <div className="w-100 flex-col gap-3 items-center">
                <button
                  className={`button-generate `}
                  disabled={canClickableButton}
                  onClick={stake}
                >
                  {t('staking.form.stake')}
                </button>
                <button
                  className=" button-generate main-btn-color"
                  onClick={handleGoStakingHistory}
                >
                  {t('staking-history.header.staking-history')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )
  );
};

export default StakingFormOffChain;
